import type { VendorAndChannelType } from '@/api/vendor';

import { type UserType } from '../user/userType';

/** Keys of T whose values are assignable to number. */
export type NumericKeys<T> = {
  [K in keyof T]: T[K] extends number ? K : never;
}[keyof T];

export type BaseMetadataType = {
  id: string;
  name: string;
  description?: string;
  created_at?: string;
  created_by?: UserType;
  updated_at?: string;
  updated_by?: UserType;
};

export type VisitTrafficType = {
  allVisits: number;
  botVisits: number;
  otherVisits: number;
  targetVisits: number;
  unresolvedVisits: number;
};

export type ActivityDataType = VisitTrafficType & {
  adClicks: number;
  adImpressions: number;
  allVisitsBest: boolean;
  allVisitsWorst: boolean;
  baseAccounts: number;
  closedWonFirstTouchAttribution: number;
  closedWonInfluenced: number;
  closedWonLinearAttribution: number;
  companiesReached: number;
  countCampaigns: number;
  countClosedwonInfluenced: number;
  countPipelineInfluenced: number;
  customImpactScore: number;
  engagedCompanies: number;
  engagedCompaniesBest: boolean;
  engagedCompaniesWorst: boolean;
  fitScore: number;
  fitScoreBest: boolean;
  fitScoreWorst: boolean;
  leadsInfluenced: number;
  opportunitiesInfluenced: number;
  opportunityInfluencedBest: boolean;
  opportunityInfluencedSumClosedWonCustom: number;
  opportunityInfluencedSumPipelineCustom: number;
  opportunityInfluencedWorst: boolean;
  percentOfEngagedCompanies: number;
  percentOpportunitiesInfluenced: number;
  pipelineFirstTouchAttribution: number;
  pipelineInfluenced: number;
  pipelineInfluencedBest: boolean;
  pipelineInfluencedPerSpend: number;
  pipelineInfluencedWorst: boolean;
  pipelineLastTouchAttribution: number;
  pipelineLinearAttribution: number;
  spend: number;
  spendPerTargetVisit: number;
  spendPerVisitBest: boolean;
  spendPerVisitWorst: boolean;
  botViewThroughVisits: number;
  otherViewThroughVisits: number;
  targetViewThroughVisits: number;
  unresolvedViewThroughVisits: number;
  viewThroughVisits: number;
  viewThroughPercent: number;
  visitEfficiency: number;
};

export type OpportunityActivityDataType = {
  audienceVisits: number;
  closedWonInfluenced: number;
  pipelineInfluenced: number;
  closedWonFirstTouchAttribution: number;
  closedWonLinearAttribution: number;
  opportunityInfluencedSumClosedWonCustom: number;
  opportunityInfluencedSumPipelineCustom: number;
  pipelineFirstTouchAttribution: number;
  pipelineLastTouchAttribution: number;
  pipelineLinearAttribution: number;
};

export type OptionType = {
  id: string;
  name?: string;
  disabled?: boolean;
};

export type SelectableOptionType = OptionType & {
  selected: boolean;
};

export type PagingOptionType = {
  results: OptionType[];
  hasNext: boolean;
};

export type CompanyType = {
  id: string;
  name: string;
  tld: string | null;
  account?: AccountType;
};

export type AccountType = {
  id: string;
  name: string;
  tld: string | null;
  company: CompanyType | null;
};

export type SectorType = OptionType;

export type RevenueRangeType = OptionType & {
  id: REVENUE_RANGE_ID;
  shortName?: string;
  name?: string;
};

export type RegionType = OptionType;

export enum TRAFFIC_TYPES {
  ALL = 'all',
  AUDIENCE = 'audience',
  BOT = 'bot',
  OTHER = 'other',
  TAM = 'tam',
  TARGET = 'target',
  UNRESOLVED = 'unresolved',
}

export enum REVENUE_RANGE_ID {
  ENTERPRISE = 'rrng_enterprise',
  MID_MARKET = 'rrng_mid_market',
  SMALL_MEDIUM = 'rrng_small_medium',
  VERY_SMALL = 'rrng_very_small',
}

export type DisplayNamesGQLResponse = {
  displayNames: {
    get: {
      id: string;
      name: string;
    }[];
  };
};

export type CSVJobStatus = {
  id: string;
  status: 'completed' | 'error' | 'processing' | 'unknown';
};

export type CSVJobBegin = {
  id: string;
  status: 'completed' | 'error' | 'processing' | 'unknown';
  url: string;
};

export type DownloadCSVBeginGQLResponse = {
  csv: {
    begin: CSVJobBegin;
  };
};

export type DownloadCSVStatusGQLResponse = {
  csv: {
    status: CSVJobStatus;
  };
};

export type VisitTrafficTrendType = VisitTrafficType & {
  sundayOfWeek: string;
};

export type TrafficVisitTrendGQLResponse = {
  visualization: {
    trafficVisitTrend2: VisitTrafficTrendType[];
  };
};

export type TrafficVisitTrendWithPreviousPeriodGQLResponse = {
  visualization: {
    currentPeriod: VisitTrafficTrendType[];
    previousPeriod: VisitTrafficTrendType[];
  };
};

export type TrafficKPIType = {
  id: string;
  label: string;
  value: number | undefined;
  valueLink?: string | false;
  lastPeriod?: number | null;
  percentChange?: number | null;
  valueFormat: {
    isCurrency?: boolean;
    precision?: number;
  };
};

export type TrafficVisitTrendByVendorGQLResponse = {
  visualization: {
    trafficVisitTrend3: {
      sundayOfWeek: string;
      allVisits: number;
      vendor: VendorAndChannelType;
    }[];
  };
};

export type TrafficDetailKpisGQLResponse = {
  visualization: {
    trafficKpi2: {
      closedWonInfluenced: number;
      closedWonOpportunities: number;
      engagedCompanies: number;
      engagedCompaniesLastPeriod: number;
      engagedCompaniesPercentChange: number;
      percentEfficiency: number;
      pipelineInfluenced: number;
      pipelineOpportunities: number;
      roms: number;
      spend: number;
      visits: number;
      visitsLastPeriod: number;
      visitsPercentChange: number;
    };
  };
};

export type ROMSByObjectIdsGQLResponse = {
  visualization: {
    campaignRoms: {
      channelAverageRoms: number;
      industryAverageRoms: number;
      roms: number;
    };
  };
};

export type TrafficVisitBreakdownGQLResponse = {
  visualization: {
    trafficVisitBreakdown2: (VisitTrafficType & {
      audienceVisits: number;
      tamVisits: number;
      otherNonAudienceVisits: number;
      otherNonTamVisits: number;
    })[];
  };
};
